import { once } from 'lodash';
import React, { createContext, useContext, useRef } from 'react';
import { useFormContext } from './FormContext';
const createFormDragContext = once(() => createContext(null));
// export const FormDragContext = createContext<FormDragContextState>(null!);
export const useFormDragContext = () => {
    const context = useContext(createFormDragContext());
    if (!context) {
        throw new Error('useFormDragContext must be used within a FormDragProvider');
    }
    return context;
};
/**
 * Implemented a custom Form Element dragging logic due to the grid layout structure, which
 * open source dragging solutions don't handle or improperly handle different sized elements
 */
export const FormDragProvider = ({ children }) => {
    const { formState, setFormState, notifyFormEdit } = useFormContext();
    const formEditPanelRef = useRef(null);
    const draggingElementRef = useRef();
    const originOffsetRef = useRef({ x: 0, y: 0 });
    const startPositionRef = useRef({ x: 0, y: 0 });
    const clonedNodeRef = useRef();
    const previousMouseOverHtmlElementRef = useRef();
    const previousMouseOverElementRef = useRef();
    const timeoutRef = useRef(0);
    const sourceStateNodeRef = useRef();
    const didMoveElementRef = useRef(false);
    const removeClonedNode = () => {
        var _a;
        clearTimeout(timeoutRef.current);
        (_a = clonedNodeRef.current) === null || _a === void 0 ? void 0 : _a.remove();
        clonedNodeRef.current = undefined;
    };
    const handleMouseDown = (event, stateNode, element) => {
        const mouseEvent = event;
        if (mouseEvent.button > 0) {
            return; // only drag on left mouse
        }
        const touchEvent = event;
        startPositionRef.current = {
            x: touchEvent.touches ? touchEvent.touches[0].clientX : mouseEvent.pageX,
            y: touchEvent.touches ? touchEvent.touches[0].clientY : mouseEvent.pageY
        };
        const domElement = event.currentTarget;
        setFormState(Object.assign(Object.assign({}, formState), { selectedFormElement: element }));
        didMoveElementRef.current = false;
        sourceStateNodeRef.current = stateNode;
        draggingElementRef.current = domElement;
        removeClonedNode();
        const clonedNode = domElement.cloneNode(true);
        clonedNodeRef.current = clonedNode;
        domElement.parentNode.appendChild(clonedNodeRef.current);
        originOffsetRef.current = {
            x: domElement.offsetLeft,
            y: domElement.offsetTop
        };
        clonedNodeRef.current.setAttribute('style', `position: absolute; top: ${originOffsetRef.current.y}px; left: ${originOffsetRef.current.x}px; min-width: ${domElement.clientWidth}px; pointer-events: none;`);
        const onMouseMove = (event) => {
            // move form element with mouse cursor
            const mouseEvent = event;
            const touchEvent = event;
            const position = {
                x: touchEvent.touches
                    ? touchEvent.touches[0].clientX
                    : mouseEvent.pageX,
                y: touchEvent.touches ? touchEvent.touches[0].clientY : mouseEvent.pageY
            };
            const deltaX = originOffsetRef.current.x + (position.x - startPositionRef.current.x);
            const deltaY = originOffsetRef.current.y + (position.y - startPositionRef.current.y);
            clonedNode.setAttribute('class', `${clonedNode.className} selected`);
            clonedNode.setAttribute('style', `position: absolute; top: ${deltaY}px; left: ${deltaX}px; min-width: ${clonedNodeRef.current.clientWidth}px; pointer-events: none; z-index: 1;`);
        };
        const onMouseUp = (event) => {
            var _a, _b;
            // animate to destination
            const dragginElement = draggingElementRef.current;
            const offsetX = ((_a = previousMouseOverHtmlElementRef.current) === null || _a === void 0 ? void 0 : _a.offsetLeft) ||
                (dragginElement === null || dragginElement === void 0 ? void 0 : dragginElement.offsetLeft);
            const offsetY = ((_b = previousMouseOverHtmlElementRef.current) === null || _b === void 0 ? void 0 : _b.offsetTop) ||
                (dragginElement === null || dragginElement === void 0 ? void 0 : dragginElement.offsetTop);
            if (typeof offsetX !== 'undefined' && typeof offsetY !== 'undefined') {
                clonedNode.setAttribute('style', `transition: 150ms; position: absolute; top: ${offsetY}px; left: ${offsetX}px; min-width: ${clonedNodeRef.current.clientWidth}px; pointer-events: none; z-index: 1;`);
                clonedNode.addEventListener('transitionend', removeClonedNode);
            }
            // in case the transition doesn't occur due to fast clicking
            timeoutRef.current = setTimeout(() => removeClonedNode(), 200);
            sourceStateNodeRef.current = undefined;
            draggingElementRef.current = undefined;
            previousMouseOverHtmlElementRef.current = undefined;
            previousMouseOverElementRef.current = undefined;
            // remove listeners on mouse up
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            document.removeEventListener('touchmove', onMouseMove);
            document.removeEventListener('touchend', onMouseUp);
            if (didMoveElementRef.current) {
                notifyFormEdit();
            }
        };
        // add listeners on mouse down
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
        document.addEventListener('touchmove', onMouseMove);
        document.addEventListener('touchend', onMouseUp);
    };
    const moveClonedNode = (event, sectionNode, stateNode) => {
        sectionNode.appendChild(clonedNodeRef.current);
        originOffsetRef.current.y = event.currentTarget.offsetTop;
        originOffsetRef.current.x = event.currentTarget.offsetLeft;
        const mouseEvent = event;
        const touchEvent = event;
        startPositionRef.current = {
            x: touchEvent.touches ? touchEvent.touches[0].clientX : mouseEvent.pageX,
            y: touchEvent.touches ? touchEvent.touches[0].clientY : mouseEvent.pageY
        };
        sourceStateNodeRef.current = stateNode;
    };
    const handleElementMouseOver = (event, stateNode, element) => {
        var _a, _b, _c, _d, _e, _f;
        // when dragging over another form element
        if (!draggingElementRef.current) {
            return;
        }
        if (previousMouseOverElementRef.current === element) {
            return;
        }
        previousMouseOverElementRef.current = element;
        previousMouseOverHtmlElementRef.current = event.currentTarget;
        const elements = (_b = (_a = sourceStateNodeRef.current) === null || _a === void 0 ? void 0 : _a.meta) === null || _b === void 0 ? void 0 : _b.elements;
        const selectedElement = formState.selectedFormElement;
        if (elements && selectedElement) {
            const sourceIndex = elements.indexOf(selectedElement);
            const destinationIndex = ((_d = (_c = stateNode.meta) === null || _c === void 0 ? void 0 : _c.elements) === null || _d === void 0 ? void 0 : _d.indexOf(element)) || 0;
            elements.splice(sourceIndex, 1);
            // insert element into new position (could be in another section)
            (_f = (_e = stateNode.meta) === null || _e === void 0 ? void 0 : _e.elements) === null || _f === void 0 ? void 0 : _f.splice(destinationIndex, 0, selectedElement);
            if (sourceStateNodeRef.current !== stateNode) {
                // switching sections
                moveClonedNode(event, event.currentTarget.parentNode, stateNode);
            }
            setFormState(Object.assign(Object.assign({}, formState), { selectedStateNode: stateNode }));
            didMoveElementRef.current = true;
        }
    };
    const handleSectionMouseOver = (event, stateNode) => {
        var _a, _b, _c, _d, _e, _f;
        // when dragging over a section check if section is empty and add the item as the first item
        if (((_b = (_a = stateNode.meta) === null || _a === void 0 ? void 0 : _a.elements) === null || _b === void 0 ? void 0 : _b.length) !== 0) {
            return;
        }
        if (!draggingElementRef.current) {
            return;
        }
        // empty section so move element into section
        const elements = (_d = (_c = sourceStateNodeRef.current) === null || _c === void 0 ? void 0 : _c.meta) === null || _d === void 0 ? void 0 : _d.elements;
        const selectedElement = formState.selectedFormElement;
        if (elements && selectedElement) {
            const sourceIndex = elements.indexOf(selectedElement);
            elements.splice(sourceIndex, 1);
            (_f = (_e = stateNode.meta) === null || _e === void 0 ? void 0 : _e.elements) === null || _f === void 0 ? void 0 : _f.push(selectedElement);
            moveClonedNode(event, event.currentTarget, stateNode);
        }
    };
    const FormDragContext = createFormDragContext();
    return (React.createElement(FormDragContext.Provider, { value: {
            formEditPanelRef,
            dragElementProps: {
                onMouseDown: handleMouseDown,
                onTouchStart: handleMouseDown,
                onMouseOver: handleElementMouseOver,
                onTouchMove: handleElementMouseOver
            },
            dragSectionProps: {
                onMouseOver: handleSectionMouseOver,
                onTouchMove: handleSectionMouseOver
            }
        } }, children));
};
export default FormDragProvider;
//# sourceMappingURL=FormDragContext.js.map